/**
 * Get greeting message based on current time of day
 * @returns {string} Greeting message in Vietnamese
 */
export function getGreetingMessage(): string {
  const hour = new Date().getHours();
  
  if (hour >= 5 && hour < 12) {
    return "Chào buổi sáng";
  } else if (hour >= 12 && hour < 14) {
    return "Chào buổi trưa";
  } else if (hour >= 14 && hour < 18) {
    return "Chào buổi chiều";
  } else if (hour >= 18 && hour < 22) {
    return "Chào buổi tối";
  } else {
    return "Chào đêm khuya";
  }
}

/**
 * Get greeting message with user name
 * @returns {string} Personalized greeting message
 */
export function getPersonalizedGreeting(): string {
  return getGreetingMessage();
}

/**
 * Get greeting message with emoji
 * @returns {string} Greeting message with emoji
 */
export function getGreetingWithEmoji(): string {
  const hour = new Date().getHours();
  let emoji = "👋";
  
  if (hour >= 5 && hour < 12) {
    emoji = "🌅";
  } else if (hour >= 12 && hour < 14) {
    emoji = "☀️";
  } else if (hour >= 14 && hour < 18) {
    emoji = "🌤️";
  } else if (hour >= 18 && hour < 22) {
    emoji = "🌆";
  } else {
    emoji = "🌙";
  }
  
  const greeting = getGreetingMessage();
  return `${emoji} ${greeting}`;
}

/**
 * Get motivational message for outlet
 * @returns {string} Random motivational message
 */
export function getMotivationalMessage(): string {
  const messages = [
    "Chúc mở hàng rôm rả nha cô chú! 🍀",
    "Pepsi mát lạnh – bán nhanh tay nào! 🧊",
    "Kệ gọn là khách mê liền! ✨",
    "Khuyến mãi ngon, nhớ nhắc khách nhé! 🎁",
    "Tủ mát đầy Pepsi, 7UP sẵn sàng! 📦",
    "Nhập đúng lúc – lời gọn lẹ! ✅",
    "Khách quen ghé, cười tươi đón chào! 😀",
    "Chốt sổ gọn cuối ngày nhẹ đầu! 📒",
    "Tiền lẻ sẵn, tính tiền vèo vèo! 💵",
    "Giá dán rõ ràng – bán nhanh hơn! 🏷️",
    "Mệt làm ly nước mát rồi bán tiếp! 🥤",
    "Sáng dọn kệ, chiều chốt đơn rộn ràng! 📈",
    "Trưng bày đẹp là khách thích! 📸",
    "Hôm nay chắc chắn đắt hàng! 🛒",
    "Hàng đủ - tủ mát đầy - Khách mê! 🤩",
    "Thiếu hàng? Có Ngọc AI lo chi! 🤖",
    "Bán vui vẻ, khách vui vẻ! 🫶",
    "Cửa hàng sạch - đẹp, ai cũng khoái! 🌿",
    "Kết ngày gọn gàng, mai bán sung hơn! 🌙"
  ];
  
  const randomIndex = Math.floor(Math.random() * messages.length);
  return messages[randomIndex];
}

/**
 * Get personalized greeting with motivational message
 * @returns {string} Greeting with motivational message
 */
export function getGreetingWithMotivation(): string {
  const greeting = getGreetingMessage();
  const motivation = getMotivationalMessage();
  
  return `${greeting}! ${motivation}`;
}

/**
 * Get time-based motivational message
 * @returns {string} Time-appropriate motivational message
 */
export function getTimeBasedMotivation(): string {
  const hour = new Date().getHours();
  
  if (hour >= 5 && hour < 12) {
    const morningMessages = [
      "Hãy bắt đầu ngày mới với năng lượng tích cực! 🌅",
      "Buổi sáng là thời điểm tốt nhất để lập kế hoạch! 📋",
      "Hãy tận dụng năng lượng buổi sáng! ⚡",
      "Một ngày mới, một cơ hội mới! ✨",
      "Hãy làm cho buổi sáng trở nên ý nghĩa! 🌟"
    ];
    return morningMessages[Math.floor(Math.random() * morningMessages.length)];
  } else if (hour >= 12 && hour < 14) {
    const noonMessages = [
      "Hãy nghỉ ngơi và nạp năng lượng! ☀️",
      "Buổi trưa là lúc để tái tạo sức lực! 💪",
      "Hãy ăn uống đầy đủ để có năng lượng! 🍽️",
      "Nghỉ trưa tốt sẽ giúp bạn làm việc hiệu quả hơn! 😴",
      "Hãy tận hưởng giờ nghỉ trưa! 🌤️"
    ];
    return noonMessages[Math.floor(Math.random() * noonMessages.length)];
  } else if (hour >= 14 && hour < 18) {
    const afternoonMessages = [
      "Buổi chiều là lúc để tập trung cao độ! 🌤️",
      "Hãy hoàn thành những việc quan trọng! 🎯",
      "Buổi chiều là thời điểm vàng để làm việc! ⏰",
      "Hãy tận dụng năng lượng buổi chiều! 💪",
      "Mọi thứ đang diễn ra tốt đẹp! 🌟"
    ];
    return afternoonMessages[Math.floor(Math.random() * afternoonMessages.length)];
  } else if (hour >= 18 && hour < 22) {
    const eveningMessages = [
      "Buổi tối là lúc để tổng kết ngày làm việc! 🌆",
      "Hãy nghỉ ngơi và thư giãn! 😌",
      "Bạn đã làm việc rất tốt hôm nay! 👏",
      "Hãy tận hưởng thời gian buổi tối! 🌙",
      "Ngày mai sẽ còn tốt hơn nữa! 🌟"
    ];
    return eveningMessages[Math.floor(Math.random() * eveningMessages.length)];
  } else {
    const nightMessages = [
      "Hãy nghỉ ngơi để có sức khỏe tốt! 🌙",
      "Giấc ngủ ngon sẽ giúp bạn khỏe mạnh! 😴",
      "Hãy tắt màn hình và đi ngủ sớm! 💤",
      "Ngày mai sẽ là một ngày mới tuyệt vời! ✨",
      "Hãy chăm sóc bản thân! 💚"
    ];
    return nightMessages[Math.floor(Math.random() * nightMessages.length)];
  }
}
